import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { queryKeys } from '../lib/queryKeys';
import * as db from '../services/dbService';
import { Category, DayEntry, MonthlyHabitData, HabitType } from '../types';
import { getDaysInMonth } from '../utils/dateUtils';
import { calculateCheckboxCompletion, calculateRatingPercentage } from '../utils/percentageUtils';

interface EntryMutationVars {
  habitId: string;
  year: number;
  month: number;
  day: number;
  update: { completed?: boolean; completedAt?: string | null; rating?: number | null; text?: string };
}

function normalizeEntryUpdate(
  update: EntryMutationVars['update'],
): Omit<EntryMutationVars['update'], 'rating'> & { rating?: number } {
  const { rating, ...rest } = update;

  if (rating === undefined) {
    return rest;
  }

  return {
    ...rest,
    rating: rating ?? undefined,
  };
}

function applyEntryUpdate(
  data: Record<string, MonthlyHabitData>,
  { habitId, year, month, day, update }: EntryMutationVars,
): Record<string, MonthlyHabitData> {
  const key = `${habitId}-${year}-${month}`;
  const existing = data[key] ?? { habitId, month, year, entries: [] };
  const idx = existing.entries.findIndex(e => e.day === day);
  const normalizedUpdate = normalizeEntryUpdate(update);
  const newEntries: DayEntry[] = idx >= 0
    ? existing.entries.map((entry, entryIndex) => {
        if (entryIndex !== idx) return entry;

        return {
          ...entry,
          ...normalizedUpdate,
          completedAt: update.completedAt !== undefined ? update.completedAt : entry.completedAt,
        };
      })
    : [
        ...existing.entries,
        {
          day,
          ...normalizedUpdate,
        },
      ];
  return { ...data, [key]: { ...existing, entries: newEntries } };
}

export function useEntries(
  userId: string,
  year: number,
  categories: Category[],
  range?: { from: string; to: string },
) {
  const qc = useQueryClient();
  const key = queryKeys.entries(userId, range ? `${range.from}:${range.to}` : String(year));

  const { data: monthlyData = {}, isLoading } = useQuery({
    queryKey: key,
    queryFn: () =>
      range ? db.fetchEntriesByRange(range.from, range.to) : db.fetchEntriesByYear(year),
    staleTime: 5 * 60 * 1000,
  });

  const mutation = useMutation({
    mutationFn: ({ habitId, year: y, month, day, update }: EntryMutationVars) =>
      db.upsertEntry(habitId, y, month, day, update),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Record<string, MonthlyHabitData>>(key);
      qc.setQueryData<Record<string, MonthlyHabitData>>(key, old =>
        applyEntryUpdate(old ?? {}, vars)
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
  });

  const upsertEntry = useCallback((
    habitId: string,
    y: number,
    month: number,
    day: number,
    update: { completed?: boolean; completedAt?: string | null; rating?: number | null; text?: string },
  ) => {
    mutation.mutate({ habitId, year: y, month, day, update });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, year]);

  const getHabitEntries = useCallback((habitId: string, month: number, targetYear: number): DayEntry[] => {
    const k = `${habitId}-${targetYear}-${month}`;
    return monthlyData[k]?.entries ?? [];
  }, [monthlyData]);

  const getHabitMonthlyPercentage = useCallback((
    habitId: string,
    month: number,
    habitType: HabitType,
  ): number => {
    const k = `${habitId}-${year}-${month}`;
    const data = monthlyData[k];
    if (!data) return 0;
    const totalDays = getDaysInMonth(month, year);
    return habitType === 'checkbox'
      ? calculateCheckboxCompletion(data.entries, totalDays)
      : calculateRatingPercentage(data.entries);
  }, [monthlyData, year]);

  // yearlyData: one percentage per month per habit — drives the yearly tracker grid
  const yearlyData = useMemo<Record<string, number[]>>(() => {
    const allHabits = categories.flatMap(c => c.habits);
    const result: Record<string, number[]> = {};
    for (const habit of allHabits) {
      result[habit.id] = Array.from({ length: 12 }, (_, m) => {
        const k = `${habit.id}-${year}-${m}`;
        const d = monthlyData[k];
        if (!d) return 0;
        const totalDays = getDaysInMonth(m, year);
        return habit.type === 'checkbox'
          ? calculateCheckboxCompletion(d.entries, totalDays)
          : calculateRatingPercentage(d.entries);
      });
    }
    return result;
  }, [monthlyData, categories, year]);

  return { monthlyData, yearlyData, isLoading, upsertEntry, getHabitEntries, getHabitMonthlyPercentage };
}
